<script setup>
import VLoaderRing from '../vendor/VLoaderRing.vue';
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useVideoAutoplay } from '../composables/useVideoAutoplay';


const props = defineProps({
  videoSrc: {
    type: String,
    required: true,
  },
  src: {
    type: String,
    default: "",
  },
  autoplay: {
    type: Boolean,
    default: false,
  },
  width: {
    type: String,
    default: "100%",
  },
  height: {
    type: String,
    default: "100%",
  },
  muted: {
    type: Boolean,
    default: true,
  },
  loop: {
    type: Boolean,
    default: false,
  },
  controls: {
    type: Boolean,
    default: false,
  },
  cover: {
    type: Boolean,
    default: true,
  },
  options: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["load", "error", "play", "pause", "ended"]);

const videoRef = ref(null);
const loading = ref(true);
const isVisible = ref(false);

// Определяем эффективный URL источника видео
const effectiveVideoSrc = computed(() => {
  const src = props.videoSrc || props.src;
  return src;
});

// Стили для компонента
const computedStyle = computed(() => {
  /** @type {Record<string, string>} */
  const style = {};

  if (props.options?.borderRadius && props.options.borderRadius > 0) {
    style.borderRadius = `${props.options.borderRadius}px`;
  }

  return style;
});

// Классы для компонента
const computedClass = computed(() => {
  const classes = {
    "v-video": true,
    visible: isVisible.value,
    "regular-video": true,
    "cover-mode": props.cover,
  };

  // Добавляем border-radius через Tailwind arbitrary values
  if (props.options?.borderRadius && props.options.borderRadius > 0) {
    classes[`rounded -[${props.options.borderRadius}px]`] = true;
  }

  return classes;
});

// Обработчики для обычного видео
function onVideoLoad() {
  if (!isVisible.value) {
    loading.value = false;
    isVisible.value = true;
    emit("load");
  }
}

function onVideoError(event) {
  loading.value = false;
  console.error("VLessonVideoCard: Video error:", event);
  console.error("VLessonVideoCard: Video src:", effectiveVideoSrc.value);
  emit("error", event);
}

function onVideoPlay() {
  emit("play");
}

function onVideoPause() {
  emit("pause");
}

function onVideoEnded() {
  emit("ended");
}

// Методы для управления воспроизведением
function play() {
  if (videoRef.value) {
    videoRef.value.play();
  }
}

function pause() {
  if (videoRef.value) {
    videoRef.value.pause();
  }
}

function stop() {
  if (videoRef.value) {
    videoRef.value.pause();
    videoRef.value.currentTime = 0;
  }
}

const aspectRatio = computed(() => {
  return props.width / props.height;
});

// Экспортируем методы
defineExpose({
  play,
  pause,
  stop,
});

useVideoAutoplay(videoRef);

onMounted(() => {
  isVisible.value = true;
});

onUnmounted(() => {});
</script>

<template>
  <div class="v-video-container" :class="computedClass" :style="computedStyle">
    <!-- Loader -->
    <VLoaderRing v-if="loading && options?.loader" :options="{ size: 50 }" class="loader" />

    <!-- Regular Video -->

    <video
      ref="videoRef"
      class="regular-video-element"
      :src="effectiveVideoSrc"
      :autoplay="autoplay || true"
      :muted="muted || true"
      :loop="loop || true"
      :controls="controls || false"
      :playsinline="true"
      preload="none"
      :style="{ width: width, height: 'auto', aspectRatio: aspectRatio }"
    >
      Your browser does not support the video tag.
    </video>
  </div>
</template>

<style scoped>
.v-video-container {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: visible; /* Дозволяємо відео виходити за межі контейнера для зуму */
  transition: opacity 200ms cubic-bezier(0.2, 0, 0.2, 1);
  background-repeat: no-repeat;
}

.v-video-container.visible {
  opacity: 1;
}

.v-video-container:not(.visible) {
  opacity: 0;
}

.regular-video-element {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

/* Стили для cover режима */
.v-video-container.cover-mode {
  position: relative;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.v-video-container.cover-mode .regular-video-element {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.loader {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate3d(-50%, -50%, 0px);
  z-index: 10;
}
</style>
